/**
 * WasmRuntimeReceipt — compact receipt for TS-WASM theorem probes.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import type { NativeWasmBoundaryReceipt, WasmKernelValidationReceipt } from "@csk/wasm";
import type { WasmHardeningProbe } from "../domain/index.js";
import { FONT, GRAY } from "../design-system/tokens.js";
import { AlertBanner } from "./AlertBanner.js";

export interface WasmRuntimeReceiptProps {
  readonly probe: WasmHardeningProbe;
  readonly loading?: boolean;
  readonly loadError?: string | null;
  readonly contractValidation?: WasmKernelValidationReceipt;
  readonly nativeBoundary?: NativeWasmBoundaryReceipt;
}

export function WasmRuntimeReceipt({
  probe,
  loading = false,
  loadError = null,
  contractValidation,
  nativeBoundary,
}: WasmRuntimeReceiptProps): React.JSX.Element {
  const verified = probe.accepted && probe.adversarialCheckRejected;
  const backend = loading ? `${probe.backend} while native boundary loads` : probe.backend;
  const validationPassed =
    contractValidation?.probes.filter(
      (entry) => entry.governedPathAccepted && entry.forbiddenPathRefused,
    ).length ?? 0;
  const validationTotal = contractValidation?.probes.length ?? 0;
  const seam = probe.seamBridge;
  const seamVerified =
    seam.descriptorMatches &&
    seam.snfTraceAccepted &&
    seam.governedBridgeAdmitted &&
    seam.forbiddenSquareRefused &&
    seam.replayRefused;
  return (
    <AlertBanner
      tone={verified ? "success" : "warning"}
      title={`TS-WASM receipt: ${probe.theoremName}`}
      description={
        <span>
          <span style={{ fontFamily: FONT.mono }}>
            {backend} · {probe.fidelity} · {probe.trustZone}
          </span>
          <br />
          {probe.detail}
          <br />
          Seam bridge: {seam.seamOrientation} seam · witness {seam.requiredWitnessKind} ·{" "}
          {seam.forbiddenGenerator} refused · {seamVerified ? "SNF/replay ok" : "needs review"}
          {contractValidation !== undefined && (
            <>
              <br />
              Contract probes: {validationPassed}/{validationTotal}
            </>
          )}
          {nativeBoundary !== undefined && (
            <>
              <br />
              Native boundary:{" "}
              {nativeBoundary.nativeArtifactLoaded ? "artifact loaded" : "pure-TS fallback"};{" "}
              {nativeBoundary.adapterValidated
                ? "adapter validated"
                : "fallback contract validated"}
            </>
          )}
          {loadError !== null && (
            <>
              <br />
              <span style={{ color: GRAY[700] }}>{loadError}</span>
            </>
          )}
        </span>
      }
    />
  );
}
